import { getTranslation } from '../i18nlocal';

interface FriendlyError {
  message: string;
  action?: 'retry' | 'buyCredits' | 'contact' | 'none';
  actionLabel?: string;
}

const ERROR_PATTERNS: Array<{ pattern: RegExp; key: string; action: FriendlyError['action'] }> = [
  { pattern: /insufficient|balance|not enough|credits/i, key: 'errors.insufficientCredits', action: 'buyCredits' },
  { pattern: /network|timeout|ECONNREFUSED|ERR_NETWORK/i, key: 'errors.network', action: 'retry' },
  { pattern: /unauthorized|401|token.*expired|session/i, key: 'errors.session', action: 'none' },
  { pattern: /not found|404/i, key: 'errors.notFound', action: 'none' },
  { pattern: /rate.?limit|429|too many/i, key: 'errors.rateLimit', action: 'retry' },
  { pattern: /file.*large|413|payload/i, key: 'errors.fileTooLarge', action: 'none' },
  { pattern: /format|unsupported|invalid.*file/i, key: 'errors.invalidFile', action: 'none' },
  { pattern: /stripe|payment|card.*declined/i, key: 'errors.payment', action: 'contact' },
  { pattern: /replicate|prediction|model/i, key: 'errors.transcription', action: 'retry' },
];

// Pas 2.3 — mesajele se rezolvă prin i18n (cheile `errors.*` din locale,
// implicit RO). Aceste valori EN rămân DOAR fallback pentru o cheie lipsă
// într-o locală încărcată leneș, niciodată prima alegere.
const FALLBACK_MESSAGES: Record<string, string> = {
  'errors.insufficientCredits': 'Not enough credits for this operation. Buy more credits to continue.',
  'errors.network': 'Connection issue. Please check your internet and try again.',
  'errors.session': 'Your session has expired. Please sign in again.',
  'errors.notFound': 'The requested resource was not found.',
  'errors.rateLimit': 'Too many requests. Please wait a moment and try again.',
  'errors.fileTooLarge': 'File is too large. Maximum size is 5GB.',
  'errors.invalidFile': 'Unsupported file format. Please use MP3, WAV, M4A, MP4, or similar.',
  'errors.payment': 'Payment processing failed. Please try a different card or contact support.',
  'errors.transcription': 'Transcription service error. Your credits have been refunded. Please try again.',
  'errors.generic': 'Something went wrong. Please try again or contact support if the issue persists.',
  'errors.action.retry': 'Try Again',
  'errors.action.buyCredits': 'Buy Credits',
  'errors.action.contact': 'Contact Support',
};

export type ErrorTranslator = (key: string) => string;

function resolveMessage(key: string, translate: ErrorTranslator): string {
  try {
    const translated = translate(key);
    if (translated && translated !== key) return translated;
  } catch {
    // i18n not ready (early boot / tests without the plugin): fall through.
  }
  return FALLBACK_MESSAGES[key] || key;
}

function actionLabelFor(action: FriendlyError['action'], translate: ErrorTranslator): string | undefined {
  if (action === 'retry') return resolveMessage('errors.action.retry', translate);
  if (action === 'buyCredits') return resolveMessage('errors.action.buyCredits', translate);
  if (action === 'contact') return resolveMessage('errors.action.contact', translate);
  return undefined;
}

export interface ApiErrorInfo {
  status?: number;
  message?: string;
}

export function readApiError(error: unknown): ApiErrorInfo {
  if (error && typeof error === 'object') {
    const record = error as { response?: { status?: unknown; data?: { message?: unknown } } };
    const status = record.response?.status;
    const message = record.response?.data?.message;
    return {
      status: typeof status === 'number' ? status : undefined,
      message: typeof message === 'string' && message ? message : undefined,
    };
  }
  return {};
}

export function readErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const record = error as { response?: { data?: { message?: unknown } }; message?: unknown };
    const nested = record.response?.data?.message;
    if (typeof nested === 'string' && nested) return nested;
    if (typeof record.message === 'string' && record.message) return record.message;
  }
  return String(error);
}

export function getFriendlyError(
  error: unknown,
  translate: ErrorTranslator = (key) => getTranslation(key),
): FriendlyError {
  const message = readErrorMessage(error);

  for (const { pattern, key, action } of ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return {
        message: resolveMessage(key, translate),
        action,
        actionLabel: actionLabelFor(action, translate),
      };
    }
  }

  return {
    message: resolveMessage('errors.generic', translate),
    action: 'retry',
    actionLabel: actionLabelFor('retry', translate),
  };
}

export function notifyError(notify: (type: string, msg: string) => void, error: unknown): void {
  const friendly = getFriendlyError(error);
  notify('danger', friendly.message);
}
